import { format } from 'date-fns'

import { SitePermitStatus } from '~/client/graph'
import { getAttachmentFileType } from '~/client/src/shared/components/WorkflowCard/fileAttachmentHelpers'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import { getFormStatusDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import User from '~/client/src/shared/models/User'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import { WorkflowGroupingKey, WorkflowSortingKey } from './WorkflowList.types'

const SITE_COMMUNICATION_CATEGORY = 'Site Communication'

interface IGroupingStores {
  workflowTypesStore?: WorkflowTypesStore
  workflowCategoriesStore?: WorkflowCategoriesStore
  companiesStore?: CompaniesStore
  projectMembersStore?: ProjectMembersStore
  locationAttributesStore?: LocationAttributesStore
  materialsStore?: MaterialsStore
  projectsStore?: ProjectsStore
}

export default class WorkflowGroupingStore {
  private workflowTypesStore: WorkflowTypesStore | null = null
  private workflowCategoriesStore: WorkflowCategoriesStore | null = null
  private companiesStore: CompaniesStore | null = null
  private projectMembersStore: ProjectMembersStore | null = null
  private locationAttributesStore: LocationAttributesStore | null = null
  private materialsStore: MaterialsStore | null = null
  private projectsStore: ProjectsStore | null = null

  public constructor(stores?: IGroupingStores) {
    this.workflowTypesStore = stores?.workflowTypesStore || null
    this.workflowCategoriesStore = stores?.workflowCategoriesStore || null
    this.companiesStore = stores?.companiesStore || null
    this.projectMembersStore = stores?.projectMembersStore || null
    this.locationAttributesStore = stores?.locationAttributesStore || null
    this.materialsStore = stores?.materialsStore || null
    this.projectsStore = stores?.projectsStore || null
  }

  public sortWorkflows(
    workflows: Workflow[],
    sortKey: WorkflowSortingKey,
    sortOrder: SortOrder,
  ): Workflow[] {
    const sorted = [...workflows]
    const order = sortOrder === SortOrder.ASC ? 1 : -1

    sorted.sort((a, b) => {
      let comparison = 0

      switch (sortKey) {
        case WorkflowSortingKey.NAME: {
          const aName = a.getCaption(this.workflowTypesStore) || ''
          const bName = b.getCaption(this.workflowTypesStore) || ''
          comparison = aName.localeCompare(bName)
          break
        }
        case WorkflowSortingKey.CATEGORY:
          comparison = (a.categoryId || '').localeCompare(b.categoryId || '')
          break
        case WorkflowSortingKey.TYPE:
          comparison = (a.typeId || '').localeCompare(b.typeId || '')
          break
        case WorkflowSortingKey.STATUS:
          comparison = (a.status || '').localeCompare(b.status || '')
          break
        case WorkflowSortingKey.DATE:
          comparison = (a.startDate || 0) - (b.startDate || 0)
          break
        case WorkflowSortingKey.COMPANY:
          comparison = (a.companyIds[0] || '').localeCompare(
            b.companyIds[0] || '',
          )
          break
        case WorkflowSortingKey.CONTACT:
          comparison = (a.responsibleIds[0] || '').localeCompare(
            b.responsibleIds[0] || '',
          )
          break
        case WorkflowSortingKey.LOCATION:
          comparison = (a.locations[0]?.id || '').localeCompare(
            b.locations[0]?.id || '',
          )
          break
        case WorkflowSortingKey.EQUIPMENT:
          comparison = (a.equipment[0]?.id || '').localeCompare(
            b.equipment[0]?.id || '',
          )
          break
        case WorkflowSortingKey.MATERIAL:
          comparison = (a.materials[0]?.materialId || '').localeCompare(
            b.materials[0]?.materialId || '',
          )
          break
        case WorkflowSortingKey.FILE_TYPE:
          comparison = (a.attachments[0]?.fileName || '').localeCompare(
            b.attachments[0]?.fileName || '',
          )
          break
        case WorkflowSortingKey.PROJECT:
          comparison = (a.projectId || '').localeCompare(b.projectId || '')
          break
        default:
          comparison = 0
      }

      return comparison * order
    })

    return sorted
  }

  public getCategoryId(
    workflow: Workflow,
    groupingKey: WorkflowGroupingKey,
  ): string | string[] {
    switch (groupingKey) {
      case WorkflowGroupingKey.TYPE:
        return (
          workflow.getTypeOfPermitType(this.workflowTypesStore) || UNASSIGNED
        )
      case WorkflowGroupingKey.STATUS:
        return workflow.status || UNASSIGNED
      case WorkflowGroupingKey.CATEGORY:
        return workflow.categoryId || UNASSIGNED
      case WorkflowGroupingKey.DATE:
        return workflow.startDate
          ? format(workflow.startDate, 'yyyy-MM-dd')
          : UNASSIGNED
      case WorkflowGroupingKey.TIME:
        return workflow.startDate
          ? format(workflow.startDate, 'yyyy-MM-dd HH:mm')
          : UNASSIGNED
      case WorkflowGroupingKey.COMPANY:
        return this.toGroupKey(
          workflow.companyIds || [],
          id => !!this.companiesStore?.getCompanyById(id, true),
        )
      case WorkflowGroupingKey.CONTACT:
        return this.toGroupKey(
          workflow.responsibleIds || [],
          id => !!this.projectMembersStore?.getById(id),
        )
      case WorkflowGroupingKey.LOCATION:
        return this.toGroupKey(
          (workflow.locations || []).map(l => l.id),
          id => !!this.locationAttributesStore?.getById(id),
        )
      case WorkflowGroupingKey.EQUIPMENT:
        return this.toGroupKey(
          (workflow.equipment || []).map(e => e.id),
          id => !!this.locationAttributesStore?.getById(id),
        )
      case WorkflowGroupingKey.MATERIAL:
        return this.toGroupKey(
          (workflow.materials || []).map(m => m.materialId),
          id => !!this.materialsStore?.hasInstanceById(id),
        )
      case WorkflowGroupingKey.FILE_TYPE:
        return this.toGroupKey(
          (workflow.attachments || []).map(
            a => getAttachmentFileType(a.fileName, '.', true) as string,
          ),
        )
      case WorkflowGroupingKey.PROJECT:
        return workflow.projectId || UNASSIGNED
      default:
        return 'none'
    }
  }

  public getCategoryName(
    categoryId: string,
    groupingKey: WorkflowGroupingKey,
  ): string {
    if (categoryId === UNASSIGNED) {
      return Localization.translator.unassigned
    }

    switch (groupingKey) {
      case WorkflowGroupingKey.TYPE: {
        const permitType =
          this.workflowTypesStore?.getLastUpdatedTypeByType(categoryId)
        if (!permitType) return categoryId
        let prefix = ''
        if (permitType.isDeleted) {
          prefix = `[${Localization.translator.deleted.toUpperCase()}] `
        } else if (!permitType.isEnabled) {
          prefix = `[${Localization.translator.disabled.toUpperCase()}] `
        }
        return `${prefix}${permitType.name}`
      }
      case WorkflowGroupingKey.STATUS:
        return (
          getFormStatusDisplayName(categoryId as SitePermitStatus) || categoryId
        )
      case WorkflowGroupingKey.CATEGORY:
        return (
          this.workflowCategoriesStore?.getNameById(categoryId, true) ||
          categoryId
        )
      case WorkflowGroupingKey.DATE:
        try {
          const date = new Date(categoryId)
          return format(date, 'MMM d, yyyy')
        } catch {
          return categoryId
        }
      case WorkflowGroupingKey.TIME:
        try {
          const date = new Date(categoryId.replace(' ', 'T'))
          return format(date, 'MMM d, yyyy, h:mm a')
        } catch {
          return categoryId
        }
      case WorkflowGroupingKey.COMPANY:
        return this.companiesStore?.getCompanyNameById(categoryId) || categoryId
      case WorkflowGroupingKey.CONTACT: {
        const user = this.projectMembersStore?.getById(categoryId)
        return user ? User.getFullName(user) : categoryId
      }
      case WorkflowGroupingKey.LOCATION:
      case WorkflowGroupingKey.EQUIPMENT: {
        const location = this.locationAttributesStore?.getById(categoryId)
        return location?.name || categoryId
      }
      case WorkflowGroupingKey.MATERIAL: {
        const materials = this.materialsStore?.getByIds([categoryId])
        return materials?.[0]?.productName || categoryId
      }
      case WorkflowGroupingKey.FILE_TYPE:
        return categoryId.toUpperCase()
      case WorkflowGroupingKey.PROJECT: {
        const project = this.projectsStore?.getProjectById(categoryId)
        return project?.name || categoryId
      }
      default:
        return categoryId
    }
  }

  /**
   * Returns a numeric sort priority for a group.
   * Lower values sort first. Site Communication groups get priority -1.
   */
  public getGroupSortPriority(
    categoryId: string,
    groupingKey: WorkflowGroupingKey,
  ): number {
    switch (groupingKey) {
      case WorkflowGroupingKey.TYPE: {
        const permitType =
          this.workflowTypesStore?.getLastUpdatedTypeByType(categoryId)
        if (permitType) {
          const categoryName = this.workflowCategoriesStore?.getNameById(
            permitType.categoryId,
            true,
          )
          if (
            categoryName?.toLowerCase() ===
            SITE_COMMUNICATION_CATEGORY.toLowerCase()
          ) {
            return -1
          }
        }
        return 0
      }
      case WorkflowGroupingKey.CATEGORY: {
        const categoryName = this.workflowCategoriesStore?.getNameById(
          categoryId,
          true,
        )
        if (
          categoryName?.toLowerCase() ===
          SITE_COMMUNICATION_CATEGORY.toLowerCase()
        ) {
          return -1
        }
        return 0
      }
      default:
        return 0
    }
  }

  private toGroupKey(
    values: string[],
    validate?: (id: string) => boolean,
  ): string | string[] {
    if (!values.length) return UNASSIGNED
    let unique = Array.from(new Set(values))
    if (validate) {
      unique = unique.filter(validate)
      if (!unique.length) return UNASSIGNED
    }
    return unique.length === 1 ? unique[0] : unique
  }
}
